import { makeStyles } from '@material-ui/core/styles'

type BudgetEmptyStateProps = {
  onStartBudget?: () => void
}

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    padding: '40px 24px',
    height: '100%',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: 860,
  },
  iconHost: {
    marginBottom: 36,
    width: 68,
    height: 68,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    border: '1px solid rgba(233,118,86,0.08)',
    background: 'rgba(233,118,86,0.08)',
  },
  titleBlock: {
    marginBottom: 40,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    maxWidth: 620,
  },
  title: {
    fontSize: '1.75rem',
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  body: {
    fontSize: '0.9375rem',
    lineHeight: 2,
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  featureCards: {
    marginBottom: 36,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
    width: '100%',
    maxWidth: 750,
  },
  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: 152,
    borderRadius: 18,
    border: '1px solid rgba(15,23,42,0.06)',
    background: 'var(--ds-bg-surface, #fff)',
    padding: '28px 24px',
    boxShadow: '0 12px 32px rgba(15,23,42,0.04)',
  },
  featureEmoji: {
    marginBottom: 16,
    fontSize: '1.875rem',
    lineHeight: 1,
  },
  featureTitle: {
    marginBottom: 8,
    fontSize: '1rem',
    lineHeight: 1.5,
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: '0 0 8px',
  },
  featureDesc: {
    fontSize: '0.9375rem',
    lineHeight: 1.75,
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  startButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    borderRadius: 14,
    background: 'linear-gradient(to right, #f47d6b, #f68778)',
    padding: '16px 40px',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#fff',
    border: 0,
    cursor: 'pointer',
    boxShadow: '0 0 20px rgba(233,118,86,0.5)',
    transition: 'transform 160ms ease, box-shadow 220ms ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 32px rgba(233,118,86,0.34)',
    },
  },
  startNote: {
    marginTop: 20,
    fontSize: '0.9375rem',
    fontWeight: 400,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: '20px 0 0',
  },
}))

const featureCards = [
  { emoji: '💬', title: 'Guided Questions', description: 'Answer questions about your production needs' },
  { emoji: '💰', title: 'Real-time Estimates', description: 'See your budget build as you make selections' },
  { emoji: '📊', title: 'Detailed Breakdown', description: 'Get itemized costs for every production element' },
]

const BudgetEmptyState = ({ onStartBudget }: BudgetEmptyStateProps) => {
  const classes = useStyles()

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.iconHost}>
          <svg
            style={{ height: 36, width: 36, color: 'var(--color-coral-sender, #E8735A)' }}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect x="8.5" y="6.5" width="31" height="35" rx="5.5" stroke="currentColor" strokeWidth="3" />
            <rect x="14" y="12" width="20" height="6" rx="2" fill="currentColor" opacity="0.18" />
            <path d="M16 24H20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M24 24H28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M32 24H32.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M16 30H20" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M24 30H28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M32 30H32.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            <path d="M16 36H28" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>

        <div className={classes.titleBlock}>
          <h2 className={classes.title}>Ad Budget Builder</h2>
          <p className={classes.body}>
            I'll help you create a detailed production budget for your advertising project through
            a guided conversation. Answer a series of questions, and I'll generate an accurate
            estimate with itemized costs.
          </p>
        </div>

        <div className={classes.featureCards}>
          {featureCards.map((card) => (
            <article key={card.title} className={classes.featureCard}>
              <div className={classes.featureEmoji} aria-hidden="true">{card.emoji}</div>
              <h3 className={classes.featureTitle}>{card.title}</h3>
              <p className={classes.featureDesc}>{card.description}</p>
            </article>
          ))}
        </div>

        <button
          type="button"
          className={classes.startButton}
          onClick={onStartBudget}
        >
          <span>Start Budget Builder</span>
          <svg style={{ height: 16, width: 16 }} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M5.5 3.5L10 8L5.5 12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <p className={classes.startNote}>Takes approximately 3-5 minutes to complete</p>
      </div>
    </div>
  )
}

export default BudgetEmptyState
